import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  TemplateRef,
  ElementRef,
  ViewChild
} from '@angular/core';
import { Observable, BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import {
  map,
  startWith,
  debounceTime,
  distinctUntilChanged
} from 'rxjs/operators';

export interface GroupedItem {
  group: string;
  items: any[];
}

@Component({
  selector: 'muni-search-input',
  template: `
    <div class="search-input-wrapper">
      <div class="input-row">
        <input
          #inputRef
          type="text"
          class="search-input"
          [placeholder]="placeholder"
          [(ngModel)]="inputValue"
          (ngModelChange)="onInputChange($event)"
          (focus)="onFocus()"
          (blur)="onInputBlur()"
          autocomplete="off"
        />
        <button
          type="button"
          *ngIf="inputValue && isFocused"
          class="clear-btn"
          (mousedown)="clearSelection()"
          aria-label="Clear search"
          title="Clear"
        >
          &times;
        </button>
      </div>

      <div
        class="results-dropdown"
        [style.display]="showDropdown ? 'block' : 'none'"
        [style.top.px]="dropdownTop"
        [style.left.px]="dropdownLeft"
        [style.width.px]="dropdownWidth"
      >
        <ul
          class="results-list"
          *ngIf="filteredGroups.length > 0; else noResults"
        >
          <ng-container *ngFor="let group of filteredGroups">
            <li class="group-header">
              <ng-container
                *ngIf="groupHeaderTemplate; else defaultGroupHeader"
              >
                <ng-container
                  *ngTemplateOutlet="
                    groupHeaderTemplate;
                    context: { item: { item: { value: group.group } } }
                  "
                ></ng-container>
              </ng-container>
              <ng-template #defaultGroupHeader>
                <span>{{ group.group }}s</span>
              </ng-template>
            </li>
            <li
              class="result-item"
              *ngFor="let item of group.items"
              (mousedown)="selectItem(item)"
            >
              <span class="result-label">{{ item[valueField] }}</span>
              <span class="result-badge">{{
                item.type === 'MANAGER' ? 'M' : 'A'
              }}</span>
            </li>
          </ng-container>
        </ul>
        <ng-template #noResults>
          <div class="no-results">No results found</div>
        </ng-template>
      </div>
    </div>
  `,
  styleUrls: ['./search-input.component.scss'],
  standalone: false
})
export class SearchInputComponent implements OnInit, OnDestroy {
  @Input() dataSource: Observable<any[]>;
  @Input() placeholder = 'Search...';
  @Input() valueField = 'name';
  @Input() groupField = 'type';
  @Input() groupHeaderTemplate: TemplateRef<any>;

  @Output() selectEvent = new EventEmitter<any>();

  @ViewChild('inputRef') inputRef: ElementRef<HTMLInputElement>;

  inputValue = '';
  showDropdown = false;
  isFocused = false;
  selectedItem: any = null;
  filteredGroups: GroupedItem[] = [];
  dropdownTop = 0;
  dropdownLeft = 0;
  dropdownWidth = 250;

  private readonly inputValue$ = new BehaviorSubject<string>('');
  private subscription: Subscription;

  ngOnInit(): void {
    // Dummy change detection trigger to ensure dropdown positions correctly on first focus
    this.subscription = combineLatest([
      this.dataSource,
      this.inputValue$.pipe(
        debounceTime(100),
        distinctUntilChanged(),
        startWith('')
      )
    ])
      .pipe(
        map(([items, searchValue]) => this.filterAndGroup(items, searchValue))
      )
      .subscribe((groups) => {
        this.filteredGroups = groups;
      });
  }

  onFocus(): void {
    const rect = this.inputRef.nativeElement.getBoundingClientRect();
    this.dropdownTop = rect.bottom;
    this.dropdownLeft = rect.left;
    this.dropdownWidth = rect.width;
    this.isFocused = true;
    this.showDropdown = true;
  }

  onInputChange(value: string): void {
    this.inputValue = value;
    this.inputValue$.next(value);
    this.showDropdown = true;
  }

  onInputBlur(): void {
    this.isFocused = false;
    this.showDropdown = false;
  }

  ngOnDestroy(): void {
    this.inputValue$.complete();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  selectItem(item: any): void {
    this.inputValue = item[this.valueField];
    this.selectedItem = item;
    this.showDropdown = false;
    this.selectEvent.emit([item]);
  }

  clearSelection(): void {
    this.inputValue = '';
    this.selectedItem = null;
    this.inputValue$.next('');
    this.showDropdown = true;
    this.selectEvent.emit([null]);
    setTimeout(() => this.inputRef.nativeElement.focus(), 0);
  }

  private filterAndGroup(items: any[], searchValue: string): GroupedItem[] {
    const sourceItems = Array.isArray(items) ? items : [];
    const normalizedSearch = this.normalizeSearchText(searchValue);
    const searchableItems = sourceItems.filter((item) => {
      const normalizedValue = this.normalizeSearchText(
        item?.[this.valueField]?.toString()
      );

      return !!normalizedValue;
    });

    const filtered = searchableItems.filter((item) => {
      const normalizedValue = this.normalizeSearchText(
        item?.[this.valueField]?.toString()
      );

      if (!normalizedSearch) {
        return true;
      }

      return normalizedValue.includes(normalizedSearch);
    });

    const itemsToDisplay =
      normalizedSearch && filtered.length === 0 ? searchableItems : filtered;

    const grouped = new Map<string, any[]>();

    itemsToDisplay.forEach((item) => {
      const groupKey = item[this.groupField] || 'Other';
      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, []);
      }
      grouped.get(groupKey).push(item);
    });

    return Array.from(grouped.entries()).map(([group, groupItems]) => ({
      group,
      items: groupItems
    }));
  }

  private normalizeSearchText(value: string): string {
    return (value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
